import React, { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { images } from '../../assets';
import useReveal from '../../hooks/useReveal';
import usePinnedStory from '../../hooks/usePinnedStory';
import { useSmoothScroll } from '../../motion/SmoothScroll';

const whyChooseUs = [
  {
    num: '01',
    icon: 'fas fa-drafting-compass',
    title: 'Design & Build Acumen',
    description:
      'Strong architectural clarity unified with practical structural, MEP, and constructability knowledge.',
  },
  {
    num: '02',
    icon: 'fas fa-check-circle',
    title: 'Direct Involvement',
    description:
      'Partner-level leadership from concept development and municipal drawings to final handover.',
  },
  {
    num: '03',
    icon: 'fas fa-layer-group',
    title: 'Material Honesty',
    description:
      'Honest material selection and rigorous detailing discipline that age with natural grace and character.',
  },
  {
    num: '04',
    icon: 'fas fa-project-diagram',
    title: 'Structured Coordination',
    description:
      'Proactive alignment between structural consultants, specialized vendors, and artisan craftsmen.',
  },
  {
    num: '05',
    icon: 'fas fa-tasks',
    title: 'Execution Discipline',
    description:
      'Milestone-driven project management with uncompromising site quality audits at every build stage.',
  },
  {
    num: '06',
    icon: 'fas fa-gem',
    title: 'Timeless Performance',
    description:
      'Spaces designed for enduring lifestyle longevity, proportion, and light rather than fleeting trends.',
  },
];

const missionPillars = [
  {
    title: 'Clarity',
    desc: 'Unambiguous documentation, transparent schedules, and direct communication.',
    icon: 'fas fa-eye',
  },
  {
    title: 'Craftsmanship',
    desc: 'Obsessive detailing discipline, premium finishes, and durable structural integrity.',
    icon: 'fas fa-tools',
  },
  {
    title: 'Accountability',
    desc: 'A single dedicated point of responsibility from the initial brief to handover.',
    icon: 'fas fa-shield-alt',
  },
];

const visionPillars = [
  {
    title: 'Thoughtful Design',
    desc: 'Tailored environments responding to climate, spatial flow, and client lifestyle.',
    icon: 'fas fa-pencil-ruler',
  },
  {
    title: 'Intelligent Build',
    desc: 'Integrated engineering practices that prevent site friction and ensure longevity.',
    icon: 'fas fa-cubes',
  },
  {
    title: 'Timeless Character',
    desc: 'Distinctive spatial identity that retains its elegance and value across decades.',
    icon: 'fas fa-gem',
  },
];

const processSteps = [
  {
    id: 'step-1',
    num: '01',
    phase: 'Phase 01 • Discovery',
    shortTitle: 'Listen',
    titlePrefix: 'Listen',
    titleEmphasis: 'First',
    icon: 'fas fa-comments',
    content:
      'We invest focused time in understanding client priorities, site microclimates, spatial lifestyle patterns, budget realities, and future growth trajectories.',
    tagline: 'Grounded in spatial empathy and client lifestyle mapping.',
    deliverables: [
      'Site context, orientation & sun-path analysis',
      'Program brief & functional requirement matrix',
      'Preliminary budget & schedule roadmap',
    ],
    outcome: 'Uncompromising clarity on project scope and design constraints.',
  },
  {
    id: 'step-2',
    num: '02',
    phase: 'Phase 02 • Conceptual Design',
    shortTitle: 'Design',
    titlePrefix: 'Design with',
    titleEmphasis: 'Purpose',
    icon: 'fas fa-pencil-ruler',
    content:
      'Concepts are developed through rigorous spatial logic, massing proportions, daylight optimization, and material authenticity — never superficial decoration.',
    tagline: 'Form and volume shaped by light, climate, and daily rituals.',
    deliverables: [
      'Volumetric schemes & spatial flow diagrams',
      '3D architectural visualizations & light studies',
      'Curated physical material & tactile moodboards',
    ],
    outcome: 'An architectural vision unified with engineering logic.',
  },
  {
    id: 'step-3',
    num: '03',
    phase: 'Phase 03 • Technical Resolution',
    shortTitle: 'Resolve',
    titlePrefix: 'Resolve in',
    titleEmphasis: 'Detail',
    icon: 'fas fa-drafting-compass',
    content:
      'Comprehensive construction drawings, structural integration, and specialized MEP coordination ensure seamless execution on site without costly surprises.',
    tagline: 'Zero-tolerance drawing packages ready for accurate site fabrication.',
    deliverables: [
      'Comprehensive GFC & working drawing sets',
      'Integrated structural, electrical & HVAC schemes',
      'Custom joinery, fenestration & finish schedules',
    ],
    outcome: 'Complete site-ready documentation with zero construction ambiguities.',
  },
  {
    id: 'step-4',
    num: '04',
    phase: 'Phase 04 • Construction & Craft',
    shortTitle: 'Build',
    titlePrefix: 'Build with',
    titleEmphasis: 'Discipline',
    icon: 'fas fa-hard-hat',
    content:
      'Rigorous multi-point quality inspections, vendor synchronization, and dedicated on-site supervision protect architectural fidelity and maintain build timelines.',
    tagline: 'Meticulous on-site management and artisanal craftsmanship.',
    deliverables: [
      'Multi-stage quality audits & material testing',
      'Vendor & artisan craftsman synchronization',
      'Live milestone tracking & transparent reporting',
    ],
    outcome: 'High-precision physical realization of the design intent.',
  },
  {
    id: 'step-5',
    num: '05',
    phase: 'Phase 05 • Handover & Living',
    shortTitle: 'Deliver',
    titlePrefix: 'Deliver with',
    titleEmphasis: 'Responsibility',
    icon: 'fas fa-key',
    content:
      'Projects are handed over with complete operational documentation, finish maintenance guides, warranty archives, and enduring post-occupancy care.',
    tagline: 'Effortless transition into your new architectural sanctuary.',
    deliverables: [
      'Comprehensive as-built drawings & warranty dossier',
      'Complete finishes & systems maintenance guide',
      'Post-handover quality check & lifetime support',
    ],
    outcome: 'A timeless, impeccably finished space ready to live and endure.',
  },
];

const tabs = [
  { id: 'choose', label: 'Why us' },
  { id: 'mission', label: 'Mission' },
  { id: 'vision', label: 'Vision' },
];

const AlternativeServices = () => {
  const [activeTab, setActiveTab] = useState('choose');
  const sectionRef = useRef(null);
  const pinSectionRef = useRef(null);
  const pinRef = useRef(null);
  const { scrollTo } = useSmoothScroll() || {};

  useReveal(sectionRef, '.apple-about-studio-card, .apple-about-copy, .apple-about-media-frame', {
    y: 36,
    stagger: 0.12,
  });
  usePinnedStory(pinSectionRef, pinRef);

  return (
    <section id="about" ref={sectionRef}>
      <div className="apple-section apple-section--white">
        <div className="apple-wrap apple-center">
          <span className="apple-eyebrow">About TheStudioVS</span>
          <h2 className="apple-display">
            How we <span className="apple-italic">work</span>
          </h2>
          <p className="apple-lede">
            Architecture shaped as an experience — grounded in clarity,
            craftsmanship, and accountability.
          </p>

          <div className="apple-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                className={`apple-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="apple-wrap--wide">
          <div className="apple-tab-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                {activeTab === 'choose' && (
                  <div className="apple-why-grid">
                    {whyChooseUs.map((item) => (
                      <div className="apple-why-card" key={item.num}>
                        <div className="apple-why-top">
                          <span className="apple-why-num">{item.num}</span>
                          <div className="apple-why-icon-badge">
                            <i className={item.icon} aria-hidden />
                          </div>
                        </div>
                        <h3>{item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {activeTab === 'mission' && (
                  <div className="apple-manifesto-card">
                    <div className="apple-manifesto-header">
                      <span className="apple-manifesto-badge">Our Mission</span>
                      <p className="apple-quote">
                        To deliver architecture grounded in <span className="apple-italic">clarity</span>, craftsmanship, and accountability — creating lasting value for clients, users, and the built environment.
                      </p>
                    </div>
                    <div className="apple-manifesto-pillars">
                      {missionPillars.map((p) => (
                        <div className="apple-manifesto-pillar" key={p.title}>
                          <div className="pillar-icon">
                            <i className={p.icon} aria-hidden />
                          </div>
                          <h4>{p.title}</h4>
                          <p>{p.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {activeTab === 'vision' && (
                  <div className="apple-manifesto-card">
                    <div className="apple-manifesto-header">
                      <span className="apple-manifesto-badge">Our Vision</span>
                      <p className="apple-quote">
                        To shape meaningful spaces that quietly <span className="apple-italic">elevate</span> everyday life through thoughtful design, intelligent construction, and timeless spatial character.
                      </p>
                    </div>
                    <div className="apple-manifesto-pillars">
                      {visionPillars.map((p) => (
                        <div className="apple-manifesto-pillar" key={p.title}>
                          <div className="pillar-icon">
                            <i className={p.icon} aria-hidden />
                          </div>
                          <h4>{p.title}</h4>
                          <p>{p.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="pin-story-section apple-pin" ref={pinSectionRef}>
        <div className="apple-wrap--wide">
          <div className="pin-story-head apple-center">
            <span className="apple-eyebrow">Our Methodology</span>
            <h2 className="apple-display">
              From brief to <span className="apple-italic">handover</span>
            </h2>
            <p className="apple-lede">
              A disciplined, step-by-step framework engineered to eliminate ambiguity and deliver architectural excellence.
            </p>
          </div>

          <div className="pin-story-deck-wrapper" ref={pinRef}>
            {/* Top Interactive Progress Rail */}
            <div className="pin-story-nav" role="tablist">
              <div className="pin-story-nav-track">
                <div className="pin-step-progress-fill" />
              </div>
              {processSteps.map((step, index) => (
                <div
                  key={step.id}
                  className={`pin-step-nav-btn ${index === 0 ? 'is-active' : ''}`}
                >
                  <span className="nav-step-dot" />
                  <span className="nav-step-num">{step.num}</span>
                  <span className="nav-step-label">{step.shortTitle}</span>
                </div>
              ))}
            </div>

            {/* Main Stage of Step Cards */}
            <div className="pin-story-stage">
              {processSteps.map((step) => (
                <div key={step.id} className="pin-story-step">
                  <div className="pin-card-glass">
                    {/* Left Column: Core Idea & Narrative */}
                    <div className="pin-card-main">
                      <div className="pin-card-meta">
                        <span className="pin-phase-badge">
                          <span className="pulse-dot" />
                          {step.phase}
                        </span>
                        <div className="pin-icon-box">
                          <i className={step.icon} aria-hidden />
                        </div>
                      </div>

                      <h3 className="pin-card-title">
                        {step.titlePrefix}{' '}
                        <span className="apple-italic">{step.titleEmphasis}</span>
                      </h3>

                      <p className="pin-card-desc">{step.content}</p>

                      <div className="pin-card-quote-strip">
                        <i className="fas fa-quote-left" aria-hidden />
                        <span>{step.tagline}</span>
                      </div>
                    </div>

                    {/* Right Column: Execution Specs & Deliverables */}
                    <div className="pin-card-specs">
                      <div className="pin-specs-header">
                        <span className="pin-specs-title">
                          <i className="fas fa-layer-group" aria-hidden /> Key Deliverables & Scope
                        </span>
                        <span className="pin-specs-index">{step.num} / 05</span>
                      </div>

                      <ul className="pin-deliverables-list">
                        {step.deliverables.map((item) => (
                          <li key={item}>
                            <span className="check-badge">
                              <i className="fas fa-check" aria-hidden />
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="pin-outcome-box">
                        <span className="outcome-label">Phase Outcome</span>
                        <p className="outcome-text">{step.outcome}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="apple-section apple-about-editorial-section">
        <div className="apple-wrap--wide">
          <div className="apple-about-studio-card">
            {/* Left Column: Rich Editorial Story & Philosophy */}
            <div className="apple-about-copy">
              <div className="about-card-badge">
                <span className="pulse-dot" />
                <span>Design & Build Architecture Studio</span>
              </div>

              <h3 className="about-studio-title">
                Architecture is not treated as a product — it is shaped as an <span className="apple-italic">experience</span>.
              </h3>

              <div className="about-narrative-block">
                <p className="lead">
                  Every project begins with understanding how people live, move, work, and interact with space. We translate those insights into environments that feel natural, efficient, and enduring.
                </p>
                <p className="about-subtext">
                  From concept sketches and municipal permissions to structural engineering, bespoke interior joinery, and turnkey site delivery — we unite the entire lifecycle under one disciplined roof.
                </p>
              </div>

              {/* Core Design Pillars Matrix */}
              <div className="about-values-grid">
                <div className="about-value-pill">
                  <div className="val-icon-wrap">
                    <i className="fas fa-shapes" aria-hidden />
                  </div>
                  <div className="val-text">
                    <strong>Proportion</strong>
                    <span>Harmonic spatial rhythm</span>
                  </div>
                </div>
                <div className="about-value-pill">
                  <div className="val-icon-wrap">
                    <i className="fas fa-layer-group" aria-hidden />
                  </div>
                  <div className="val-text">
                    <strong>Material Honesty</strong>
                    <span>Textures that age gracefully</span>
                  </div>
                </div>
                <div className="about-value-pill">
                  <div className="val-icon-wrap">
                    <i className="fas fa-sun" aria-hidden />
                  </div>
                  <div className="val-text">
                    <strong>Natural Light</strong>
                    <span>Atmospheric daylighting</span>
                  </div>
                </div>
                <div className="about-value-pill">
                  <div className="val-icon-wrap">
                    <i className="fas fa-cube" aria-hidden />
                  </div>
                  <div className="val-text">
                    <strong>Structural Rigor</strong>
                    <span>Engineered build integrity</span>
                  </div>
                </div>
              </div>

              {/* CTA and Stats Row */}
              <div className="about-footer-row">
                <a
                  href="#contact"
                  className="apple-cta"
                  onClick={(e) => {
                    e.preventDefault();
                    if (scrollTo) scrollTo('#contact');
                    else
                      document
                        .querySelector('#contact')
                        ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <span>Connect with our studio</span>
                  <i className="fas fa-arrow-right" aria-hidden />
                </a>
                <div className="about-studio-stat">
                  <strong>Turnkey</strong>
                  <span>Concept to Handover</span>
                </div>
              </div>
            </div>

            {/* Right Column: Layered Architectural Visual Showcase */}
            <div className="apple-about-media-frame">
              <div className="about-img-container">
                <img
                  src={images.services.transformationalDesigns}
                  alt="TheStudioVS architectural craftsmanship"
                  className="about-primary-img"
                />
                <div className="about-img-overlay" />
              </div>

              {/* Floating Glass Architectural Badge */}
              <div className="about-floating-badge">
                <div className="floating-badge-icon">
                  <i className="fas fa-compass-drafting" aria-hidden />
                </div>
                <div className="floating-badge-copy">
                  <span className="badge-tag">TheStudioVS Standard</span>
                  <p>Design Precision & Build Accountability</p>
                </div>
              </div>

              {/* Floating Stat Accent Card */}
              <div className="about-floating-stat-card">
                <span className="stat-highlight">100%</span>
                <span className="stat-sub">Integrated In-House Execution</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlternativeServices;
